// Modules:
import React, {
    useState,
    useEffect,
    useRef,
    useContext,
    useMemo,
    useCallback,
} from 'react';

// Chart.js:
import {
    Chart,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import {
    BarWithErrorBarsController,
    BarWithErrorBar,
} from 'chartjs-chart-error-bars';

// Context:
import TopicConfigContext from 'utilities/topicConfigContext';

// Config:
import config from 'config/config';

// Material UI:
import Grid from '@mui/material/Grid';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Alert from '@mui/material/Alert';

// Utils:
import { splitChartTitle } from 'utilities/utils';

// Components:
import Selector from './Selector';

Chart.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    BarWithErrorBarsController,
    BarWithErrorBar
);

const ConfidenceIntervalHorizontalBar = ({
    json,
    selection,
    stratification,
    setStratification,
    dataUnavailable,
    setDataUnavailable,
    styles,
    handleDropdownSelect,
    checkIfNull,
    setMissingSuppressedModalOpen,
    setChartInfoModalOpen,
}) => {
    // // Refs // //
    const canvasRef = useRef(null); // For canvas element
    const chartRef = useRef(null); // For Chart.js instance

    // // State // //
    const [windowSize, setWindowSize] = useState(window.innerWidth);

    // //  Variables // //
    const MAX_MOBILE_VIEW_WIDTH = 500;

    // // Context // //
    const { tab: currentTab } = useContext(TopicConfigContext);

    // // Methods // //
    const handleClick = (value) => {
        if (stratification !== value) {
            setStratification(value);
        }
    };

    const handleWindowResize = useCallback(() => {
        setWindowSize(window.innerWidth);
    }, []);

    // Method to convert missing/suppressed values to null for display in chart:
    const handleMissingSuppressed = (data) => {
        if (!data || typeof data !== 'object') {
            return { x: null, xMin: null, xMax: null };
        }

        const { x, xMin, xMax } = data;
        const invalidValues = [config.missing, config.suppressed];

        return {
            x: invalidValues.includes(x) ? null : x,
            xMin: invalidValues.includes(xMin) ? null : xMin,
            xMax: invalidValues.includes(xMax) ? null : xMax,
        };
    };

    const chartData = useMemo(() => {
        // Don't proceed unless we have data and a current tab configuration
        if (!json || !currentTab) {
            return null;
        }

        // Filter chart configurations to only those matching the selected stratification
        const filteredChartConfigs = currentTab.chartSettings.chartConfigs.filter(
            (chartInfo) => chartInfo.stratification === stratification
        );

        const labelsSet = new Set(); // Unique group labels (e.g., categories on the y-axis)
        const dataSetsMap = {}; // Maps each dataset key to its metadata and values

        filteredChartConfigs.forEach((filteredConfig) => {
            const {
                groupLabel,
                dataSetSubGroup: key,
                label,
                color,
                setName,
                stratification: strat,
                order,
            } = filteredConfig;

            labelsSet.add(groupLabel); // Collect unique labels for axis ticks

            // Create or update the dataset entry
            if (!dataSetsMap[key]) {
                dataSetsMap[key] = {
                    label,
                    backgroundColor: color,
                    data: [],
                    setNames: [setName],
                    strat,
                    order,
                };
            } else {
                // Insert additional setNames and keep them sorted alphabetically
                // to maintain consistent data-to-label alignment when rendering
                dataSetsMap[key].setNames.push(setName);
                dataSetsMap[key].setNames.sort((a, b) => a.localeCompare(b));
            }
        });

        // Populate dataset `data` arrays using cleaned values from the JSON
        Object.entries(dataSetsMap).forEach(([key, dataSet]) => {
            const workingDataArray = json[0][dataSet.strat][key];
            dataSet.setNames.forEach((setName) => {
                const cleanedData = handleMissingSuppressed(
                    workingDataArray[setName]
                );
                dataSet.data.push(cleanedData);
            });
        });

        // Convert Set to array and sort labels alphabetically to match dataset order
        const labelsArray = Array.from(labelsSet).sort((a, b) =>
            a.toLowerCase().localeCompare(b.toLowerCase())
        );

        const datasetArray = Object.values(dataSetsMap);

        // Special case: If 'Any disability (excluding hearing)' label is found,
        // split it into two lines:
        const lineBreakIndex = labelsArray.indexOf(
            'Any disability (excluding hearing)'
        );

        if (lineBreakIndex !== -1) {
            labelsArray[lineBreakIndex] = [
                'Any disability',
                '(excluding hearing)',
            ];
        }

        return {
            labels: labelsArray,
            datasets: datasetArray,
        };
    }, [json, currentTab, stratification]);

    // Chart instantiation effect
    useEffect(() => {
        setDataUnavailable(false);
        const constructChart = () => {
            const ctx = canvasRef.current?.getContext('2d');
            if (!ctx || !chartData) return;

            // Destroy previous chart if it exists
            if (chartRef.current) {
                chartRef.current.destroy();
            }

            const allNull = checkIfNull(chartData.datasets);

            if (allNull) {
                setDataUnavailable(true);
            }

            chartRef.current = new Chart(ctx, {
                type: BarWithErrorBarsController.id,
                data: chartData,
                options: {
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: {
                            display: currentTab.chartSettings.displayChartTitle,
                            text:
                                windowSize <= MAX_MOBILE_VIEW_WIDTH
                                    ? splitChartTitle(
                                          currentTab.chartSettings.chartTitle
                                      )
                                    : currentTab.chartSettings.chartTitle,
                        },
                        subtitle: {
                            padding: {
                                bottom: 10,
                            },
                            display:
                                currentTab.chartSettings.displayChartSubtitle ||
                                false, // TODO:  The OR statements for these two keys are to account for a real subtitle only being in place for one topic.  Once the subtitle is given for all charts, this can be removed.
                            text: currentTab.chartSettings.chartSubtitle || '',
                        },
                        tooltip: {
                            callbacks: {
                                label(context) {
                                    const point = context.raw;
                                    const value = point?.x?.toFixed(1);
                                    const min = point?.xMin?.toFixed(1);
                                    const max = point?.xMax?.toFixed(1);
                                    return `${value}% (95% CI: ${min}% – ${max}%)`;
                                },
                            },
                        },
                        legend: {
                            position: 'bottom',
                        },
                    },
                    scales: {
                        x: {
                            title: {
                                display:
                                    currentTab.chartSettings
                                        ?.displayChartSubtitle || false,
                                text: currentTab.chartSettings?.xAxisLabel,
                            },
                            beginAtZero: true,
                            ticks: { padding: 10, stepSize: 10 },
                        },
                        y: {
                            ticks: {
                                minRotation:
                                    // Adds a 60 degree rotation for x-axis labels. 0 means labels lay flat.
                                    windowSize <= MAX_MOBILE_VIEW_WIDTH
                                        ? 20
                                        : 0,
                            },
                        },
                    },
                },
            });
        };

        constructChart();

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
        // eslint wants the state variable windowSize included in this dependency array
        // which does not need to be included as it is only used to manage label rotation
        // and has no influence on chart construction.  Omitting intentionally.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartData]);

    // Effect to set the stratification to the default stratification in config:
    useEffect(() => {
        if (currentTab) {
            if (
                stratification === undefined ||
                // To set to default if other chart within theme is stratified
                // by a value that is not present in this charts available stratifications
                !currentTab.chartSettings?.stratifications.some(
                    ({ field }) => field === stratification
                )
            ) {
                setStratification(
                    currentTab.chartSettings?.defaultStratification
                );
            }
        }
    }, [currentTab, setStratification, stratification]);

    // Sets window width in state.  Used to set chart x-axis label rotation degree in chart.js ticks object.
    useEffect(() => {
        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, [handleWindowResize]);

    const dataYears = currentTab?.chartSettings?.chartDataYears;

    return (
        <>
            <Grid container justify="center">
                {dataYears && selection && (
                    <Selector
                        currentTab={currentTab}
                        chartDataYears={dataYears}
                        handleDropdownSelect={handleDropdownSelect}
                        selection={selection}
                        styles={styles}
                        helperText="Select Date"
                    ></Selector>
                )}
            </Grid>
            {dataUnavailable ? (
                <Grid item xs={12}>
                    <div className="data-unavailable">
                        <Alert
                            sx={styles}
                            severity="info"
                            onClick={() => setMissingSuppressedModalOpen(true)}
                        >
                            {`Data are not available to display for ${selection}.`}
                        </Alert>
                    </div>
                </Grid>
            ) : (
                <>
                    <Grid item xs={12}>
                        <canvas
                            ref={canvasRef}
                            width={100}
                            height={350}
                        ></canvas>
                    </Grid>
                    <Grid item xs={12}>
                        <div className="chart-buttons-container">
                            <ButtonGroup
                                color="primary"
                                aria-label="text primary button group"
                                sx={styles}
                            >
                                {currentTab.chartSettings?.stratifications.map(
                                    (strata) => (
                                        <Button
                                            key={strata.title}
                                            disableElevation
                                            onClick={() =>
                                                handleClick(strata.field)
                                            }
                                            variant={
                                                stratification === strata.field
                                                    ? 'contained'
                                                    : 'outlined'
                                            }
                                            sx={styles}
                                        >
                                            {strata.title}
                                        </Button>
                                    )
                                )}
                            </ButtonGroup>
                        </div>
                        <div className="chart-info-modal-trigger-container">
                            <Button
                                size="small"
                                onClick={() => setChartInfoModalOpen(true)}
                            >
                                <InfoOutlinedIcon />
                            </Button>
                        </div>
                    </Grid>
                </>
            )}
        </>
    );
};

export default ConfidenceIntervalHorizontalBar;
